import { useState, FormEvent } from "react";

const SERVICES = [
  "Residential Cleaning",
  "Commercial Cleaning",
  "Deep Cleaning",
  "Move-In / Move-Out",
  "Eco-Friendly Cleaning",
  "Window Cleaning",
];

export default function BookingForm() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const notes = (form.elements.namedItem("notes") as HTMLTextAreaElement)?.value || "";
    if (notes.length > 500) return;

    setLoading(true);
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch("https://readdy.ai/api/form/d7087rm7ipaugi04lub0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="booking"
      className="relative py-24 overflow-hidden"
      style={{
        backgroundImage:
          "url('https://public.readdy.ai/ai/img_res/be09963d090c9a57be40300bec73eac5.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-xs font-semibold uppercase tracking-widest text-teal-300 mb-3 block">
            Easy Scheduling
          </span>
          <h2
            className="text-4xl md:text-5xl font-extrabold text-white mb-4"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            Schedule Your
            <br />
            <span className="text-teal-400">Cleaning Today</span>
          </h2>
          <p className="text-white/60 text-sm max-w-md mx-auto">
            Fill out the form below and we&apos;ll confirm your booking within 2 hours.
          </p>
        </div>

        {/* Form Card */}
        {submitted ? (
          <div className="max-w-lg mx-auto bg-white rounded-2xl p-12 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-teal-50 rounded-full mx-auto mb-4">
              <i className="ri-checkbox-circle-fill text-teal-500 text-3xl" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Booking Confirmed!
            </h3>
            <p className="text-gray-500 text-sm">
              Thank you! We&apos;ve received your booking request and will contact you within 2 hours to confirm your appointment.
            </p>
            <button
              onClick={() => setSubmitted(false)}
              className="mt-6 text-sm text-teal-600 underline cursor-pointer"
            >
              Make another booking
            </button>
          </div>
        ) : (
          <form
            data-readdy-form
            onSubmit={handleSubmit}
            className="max-w-3xl mx-auto bg-white rounded-2xl p-8 md:p-10"
          >
            {/* Row 1 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1.5">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="Jane Smith"
                  className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-teal-500 transition-colors"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1.5">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="jane@email.com"
                  className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-teal-500 transition-colors"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1.5">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  required
                  placeholder="+1 (555) 000-0000"
                  className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-teal-500 transition-colors"
                />
              </div>
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1.5">Service Type *</label>
                <select
                  name="service"
                  required
                  className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-teal-500 transition-colors cursor-pointer bg-white"
                >
                  <option value="">Select a service</option>
                  {SERVICES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1.5">Preferred Date *</label>
                <input
                  type="date"
                  name="date"
                  required
                  className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-teal-500 transition-colors cursor-pointer"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1.5">Preferred Time</label>
                <select
                  name="time"
                  className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-teal-500 transition-colors cursor-pointer bg-white"
                >
                  <option value="">Any time</option>
                  <option value="morning">Morning (8AM – 12PM)</option>
                  <option value="afternoon">Afternoon (12PM – 5PM)</option>
                  <option value="evening">Evening (5PM – 8PM)</option>
                </select>
              </div>
            </div>

            {/* Address */}
            <div className="mb-4">
              <label className="text-xs font-medium text-gray-500 block mb-1.5">Property Address *</label>
              <input
                type="text"
                name="address"
                required
                placeholder="123 Main Street, Apt 4B, New York, NY 10001"
                className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-teal-500 transition-colors"
              />
            </div>

            {/* Notes */}
            <div className="mb-6">
              <label className="text-xs font-medium text-gray-500 block mb-1.5">
                Special Requests{" "}
                <span className={`ml-1 ${charCount > 480 ? "text-red-500" : "text-gray-400"}`}>
                  ({charCount}/500)
                </span>
              </label>
              <textarea
                name="notes"
                rows={3}
                maxLength={500}
                placeholder="Any pets, special access instructions, or areas to focus on..."
                className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-teal-500 transition-colors resize-none"
                onChange={(e) => setCharCount(e.target.value.length)}
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gray-900 hover:bg-teal-700 text-white font-semibold py-3.5 rounded-xl transition-colors duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-3 text-sm"
            >
              <div className="w-7 h-7 flex items-center justify-center bg-teal-500 rounded-lg">
                <i className="ri-calendar-check-line text-white text-sm" />
              </div>
              {loading ? "Submitting..." : "Confirm Booking"}
              {!loading && <i className="ri-arrow-right-line" />}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
